import { c as _c } from "react/compiler-runtime";
import { useEffect, useState, type RefObject } from "react";

/** mt-4 + featured card (illustration, copy, button, padding). */
const FEATURED_BOX_VERTICAL_RESERVE_PX = 176;
const FIT_BUFFER_PX = 8;

/**
 * Hides the sidebar Slack featured box when there isn't enough room between the
 * nav block and the footer for the card.
 *
 * `useEffect` (not `useLayoutEffect`) so the footer ref, which is attached to a
 * later sibling of the placement's subtree, is populated by the time we
 * measure on the initial mount.
 */
export function useSidebarFeaturedBoxVerticalFit(navBlockRef, footerAnchorRef, enabled) {
  const $ = _c(5);
  const [hasVerticalSpace, setHasVerticalSpace] = useState(false);
  let t0;
  let t1;
  if ($[0] !== enabled || $[1] !== footerAnchorRef || $[2] !== navBlockRef) {
    t0 = () => {
      if (!enabled) {
        setHasVerticalSpace(false);
        return;
      }
      const navEl = navBlockRef.current;
      const footerEl = footerAnchorRef.current;
      if (!navEl || !footerEl) {
        return;
      }
      let rafId = null;
      const measure = () => {
        rafId = null;
        const available = footerEl.getBoundingClientRect().top - navEl.getBoundingClientRect().bottom;
        const needed = FEATURED_BOX_VERTICAL_RESERVE_PX + FIT_BUFFER_PX;
        setHasVerticalSpace(prev => {
          const next = available >= needed;
          return prev === next ? prev : next;
        });
      };
      const scheduleMeasure = () => {
        if (rafId != null) {
          return;
        }
        rafId = requestAnimationFrame(measure);
      };
      measure();
      const observer = new ResizeObserver(scheduleMeasure);
      observer.observe(navEl);
      observer.observe(footerEl);
      window.addEventListener("resize", scheduleMeasure);
      return () => {
        if (rafId != null) {
          cancelAnimationFrame(rafId);
        }
        observer.disconnect();
        window.removeEventListener("resize", scheduleMeasure);
      };
    };
    t1 = [enabled, footerAnchorRef, navBlockRef];
    $[0] = enabled;
    $[1] = footerAnchorRef;
    $[2] = navBlockRef;
    $[3] = t0;
    $[4] = t1;
  } else {
    t0 = $[3];
    t1 = $[4];
  }
  useEffect(t0, t1);
  return hasVerticalSpace;
}